import { useEffect, useState } from "react";
import { fetchSettings, updateSettings } from "../lib/api";
import { api } from "../lib/api";
import { ShieldCheckIcon, ShieldSlashIcon, WarningIcon, BellRingingIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AI Cost Guard — Dashboard widget for token-burn control.
 *
 * Three controls in one card:
 *   1. Hard kill switch  → ai_enabled
 *   2. Daily €  cap      → ai_daily_budget_eur
 *   3. Monthly €  cap    → ai_monthly_budget_eur
 *   + anomaly threshold  → ai_anomaly_threshold_eur (€ in 1h that triggers pushover)
 *   + cost alerts toggle → ai_cost_alerts_enabled
 *
 * Props:
 *   spendEur: current month's AI spend (€)
 *   calls:    AI calls this month
 */
export default function AICostGuard({ spendEur = 0, calls = 0 }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [daily, setDaily] = useState("");
  const [monthly, setMonthly] = useState("");
  const [anomaly, setAnomaly] = useState("");

  useEffect(() => {
    fetchSettings()
      .then((s) => {
        setSettings(s);
        setDaily(String(s.ai_daily_budget_eur ?? 0.5));
        setMonthly(String(s.ai_monthly_budget_eur ?? 5));
        setAnomaly(String(s.ai_anomaly_threshold_eur ?? 1));
      })
      .catch(() => {});
  }, []);

  if (!settings) {
    return (
      <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="ai-cost-guard-loading">
        <div className="text-xs text-zinc-400">Loading AI Cost Guard…</div>
      </div>
    );
  }

  const enabled = !!settings.ai_enabled;
  const alertsOn = settings.ai_cost_alerts_enabled !== false;
  const monthlyCap = Number(settings.ai_monthly_budget_eur ?? 5);
  const dailyCap = Number(settings.ai_daily_budget_eur ?? 0.5);
  const pct = monthlyCap > 0 ? Math.min(100, (spendEur / monthlyCap) * 100) : 0;
  const overBudget = pct >= 100;
  const danger = pct >= 80 || overBudget;

  const patch = async (patchBody, successMsg) => {
    setBusy(true);
    try {
      const updated = await updateSettings({ ...settings, ...patchBody });
      setSettings(updated);
      if (successMsg) toast.success(successMsg);
      return updated;
    } catch {
      toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };

  const onToggleAI = () =>
    patch(
      { ai_enabled: !enabled },
      !enabled ? "AI fallback ENABLED" : "AI fallback DISABLED (hard kill)"
    );

  const onToggleAlerts = () =>
    patch(
      { ai_cost_alerts_enabled: !alertsOn },
      !alertsOn ? "Cost alerts ENABLED" : "Cost alerts DISABLED"
    );

  const saveCaps = async () => {
    const d = parseFloat(daily);
    const m = parseFloat(monthly);
    const a = parseFloat(anomaly);
    if ([d, m, a].some((v) => !isFinite(v) || v < 0)) {
      toast.error("All caps must be positive numbers");
      return;
    }
    await patch(
      { ai_daily_budget_eur: d, ai_monthly_budget_eur: m, ai_anomaly_threshold_eur: a },
      "Caps saved"
    );
  };

  const onTestPushover = async () => {
    setBusy(true);
    try {
      await api.post("/ai/cost-alert/test");
      toast.success("Test Pushover notification sent ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const barColor = overBudget
    ? "bg-[#FF3B30]"
    : pct >= 80
    ? "bg-amber-500"
    : "bg-emerald-500";

  const capsDirty =
    daily !== String(settings.ai_daily_budget_eur ?? "") ||
    monthly !== String(settings.ai_monthly_budget_eur ?? "") ||
    anomaly !== String(settings.ai_anomaly_threshold_eur ?? "");

  return (
    <div
      data-testid="ai-cost-guard"
      className={`border-2 ${
        overBudget ? "border-[#FF3B30]" : enabled ? "border-zinc-950" : "border-zinc-200"
      } bg-white p-5 mb-6`}
    >
      {/* Header row: kill switch + spend bar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-5 pb-5 border-b border-zinc-100">
        <div className="flex items-center gap-4 flex-shrink-0 min-w-[280px]">
          <div
            className={`w-12 h-12 flex items-center justify-center ${
              enabled
                ? overBudget
                  ? "bg-[#FF3B30] text-white"
                  : "bg-zinc-950 text-white"
                : "bg-zinc-100 text-zinc-400"
            }`}
          >
            {enabled ? (
              <ShieldCheckIcon size={26} weight="fill" />
            ) : (
              <ShieldSlashIcon size={26} weight="fill" />
            )}
          </div>
          <div>
            <div className="overline mb-1">AI Cost Guard</div>
            <div className="flex items-center gap-2.5">
              <button
                onClick={onToggleAI}
                disabled={busy}
                data-testid="ai-killswitch-toggle"
                className={`relative inline-flex h-6 w-11 items-center transition-colors ${
                  enabled ? "bg-zinc-950" : "bg-zinc-300"
                } disabled:opacity-50`}
                aria-label="Toggle AI fallback"
              >
                <span
                  className={`inline-block h-4 w-4 transform bg-white transition ${
                    enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span className="text-sm font-semibold" data-testid="ai-killswitch-state">
                {enabled ? "AI enabled" : "HARD KILL · AI disabled"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              This month · {calls} call{calls === 1 ? "" : "s"}
            </div>
            <div className="font-mono-num text-sm" data-testid="ai-cost-guard-spend">
              <span className={overBudget ? "text-[#FF3B30] font-bold" : ""}>
                €{spendEur.toFixed(4)}
              </span>
              <span className="text-zinc-400"> / €{monthlyCap.toFixed(2)}</span>
            </div>
          </div>
          <div className="h-2.5 bg-zinc-100 overflow-hidden">
            <div
              data-testid="ai-cost-guard-bar"
              className={`h-full ${barColor} transition-all`}
              style={{ width: `${Math.max(2, pct)}%` }}
            />
          </div>
          {danger && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-[#FF3B30] font-semibold">
              <WarningIcon size={13} weight="fill" />
              {overBudget
                ? "Monthly budget hit — AI auto-disabled until next cycle."
                : `${pct.toFixed(0)}% of monthly budget used`}
            </div>
          )}
        </div>
      </div>

      {/* Caps row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-5">
        <CapInput
          testid="cap-daily"
          label="Daily cap (€)"
          value={daily}
          onChange={setDaily}
          hint="AI auto-stops at this spend per 24h."
        />
        <CapInput
          testid="cap-monthly"
          label="Monthly cap (€)"
          value={monthly}
          onChange={setMonthly}
          hint="Hard ceiling per 30-day window."
        />
        <CapInput
          testid="cap-anomaly"
          label="Burn anomaly (€/h)"
          value={anomaly}
          onChange={setAnomaly}
          hint="Pushover siren if this spend in 1h."
        />
      </div>

      {/* Action row */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 mt-2 border-t border-zinc-100">
        <div className="flex items-center gap-3 text-xs">
          <span className="text-zinc-500 uppercase tracking-wider">Pushover cost alerts</span>
          <button
            onClick={onToggleAlerts}
            disabled={busy}
            data-testid="ai-cost-alerts-toggle"
            className={`relative inline-flex h-5 w-9 items-center transition-colors ${
              alertsOn ? "bg-zinc-950" : "bg-zinc-300"
            } disabled:opacity-50`}
          >
            <span
              className={`inline-block h-3 w-3 transform bg-white transition ${
                alertsOn ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
          <span className="font-semibold">{alertsOn ? "ON" : "OFF"}</span>
          <span className="text-zinc-400">· end-of-day, end-of-month, anomaly</span>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="ai-test-pushover"
            onClick={onTestPushover}
            disabled={busy || !alertsOn}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:bg-zinc-100 disabled:text-zinc-400 inline-flex items-center gap-1.5"
          >
            <BellRingingIcon size={13} weight="bold" />
            Test pushover
          </button>
          <button
            data-testid="ai-save-caps"
            onClick={saveCaps}
            disabled={busy || !capsDirty}
            className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white disabled:bg-zinc-300 disabled:cursor-not-allowed"
          >
            Save caps
          </button>
        </div>
      </div>
    </div>
  );
}

function CapInput({ label, value, onChange, hint, testid }) {
  return (
    <div>
      <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">{label}</label>
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-zinc-500">€</span>
        <input
          data-testid={testid}
          type="number"
          step="0.1"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono-num focus:outline-none focus:border-zinc-950"
        />
      </div>
      {hint && <div className="text-[11px] text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}
